import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileCheck, AlertCircle } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante il login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout">
      {/* Form Section */}
      <div className="auth-form-section">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-forest rounded-sm flex items-center justify-center">
              <FileCheck className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-xl font-bold tracking-tight text-forest">BustaControllo</span>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold tracking-tight mb-2">Bentornato</h1>
          <p className="text-muted-foreground mb-8">
            Accedi per controllare le tue buste paga
          </p>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-4 mb-6 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="label-uppercase">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@esempio.com"
                className="swiss-input h-12"
                data-testid="login-email"
                required
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="label-uppercase">Password</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-xs text-forest hover:underline"
                  data-testid="forgot-password-link"
                >
                  Password dimenticata?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="swiss-input h-12"
                data-testid="login-password"
                required
              />
            </div>

            <Button
              type="submit"
              className="swiss-btn-primary w-full h-12"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="spinner" />
                  Accesso in corso...
                </span>
              ) : (
                'Accedi'
              )}
            </Button>
          </form>

          {/* Register link */}
          <p className="mt-8 pb-20 text-center text-sm text-muted-foreground">
            Non hai un account?{' '}
            <Link 
              to="/register" 
              className="text-forest font-medium hover:underline"
              data-testid="register-link"
            >
              Registrati ora
            </Link>
          </p>
          
          {/* Copyright */}
          <p className="mt-4 text-center text-xs text-muted-foreground">
            © {new Date().getFullYear()} BustaControllo. Tutti i diritti riservati.
          </p>
        </div>
      </div>

      {/* Brand Section */}
      <div className="auth-brand-section">
        <div className="relative z-10 text-white text-center max-w-md">
          <h2 className="text-4xl font-bold mb-6">
            Verifica le tue buste paga in pochi click
          </h2>
          <p className="text-lg text-white/80">
            Controlla automaticamente i calcoli di IRPEF, INPS e netto. 
            Individua errori e anomalie nelle tue retribuzioni.
          </p>
          
          {/* Features */}
          <div className="mt-12 grid grid-cols-2 gap-4 text-left">
            <div className="bg-white/10 backdrop-blur p-4 rounded-sm">
              <div className="font-mono text-2xl mb-1">100%</div>
              <div className="text-sm text-white/70">Calcoli verificati</div>
            </div>
            <div className="bg-white/10 backdrop-blur p-4 rounded-sm">
              <div className="font-mono text-2xl mb-1">IRPEF</div>
              <div className="text-sm text-white/70">Aliquote 2024</div>
            </div>
            <div className="bg-white/10 backdrop-blur p-4 rounded-sm">
              <div className="font-mono text-2xl mb-1">9.19%</div>
              <div className="text-sm text-white/70">INPS dipendente</div>
            </div>
            <div className="bg-white/10 backdrop-blur p-4 rounded-sm">
              <div className="font-mono text-2xl mb-1">TFR</div>
              <div className="text-sm text-white/70">Controllo accantonamento</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
